<template>
  <LayupSpinner v-if="isLoading" :size="10" />
  <template v-else>
    <div class="border border-grey-light-200 rounded-lg">
      <div class="p-4 flex flex-col gap-2 sm:gap-0 sm:flex-row sm:justify-between border-b border-grey-light-200">
        <div class="flex gap-4 items-center">
          <h2 class="font-semibold text-grey-light-900">Team Members</h2>
          <Badge variant="brand">{{ users.length }} users</Badge>
        </div>
        <div class="flex gap-2">
          <Button variant="primary" type="button" class="w-auto" @click="onOpenModal('add')">Add Members</Button>
        </div>
      </div>
      <DataTable
        :columns="teamTableColumns"
        :data="users"
        :total-pages="totalPages"
        :total-count="totalCount"
        :page-number="pageNumber"
        :page-size="pageSize"
        :manual-sorting="false"
        @update:page="onUpdatePage"
      />
    </div>
  </template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogContent class="sm:max-w-[525px]">
      <DialogHeader>
        <DialogTitle>{{ selectedAction === 'add' ? 'Add' : 'Edit' }} profile</DialogTitle>
        <DialogDescription v-if="selectedAction === 'edit'">
          Make changes to your profile here. Click save when you're done.
        </DialogDescription>
        <DialogDescription v-else> Add a new user / admin below. Click save when you're done. </DialogDescription>
        <DialogDescription v-if="maxUsersReached && selectedAction === 'add'">
          <p class="text-sm font-medium text-destructive">
            Please note that you can not add a new user. The {{ props.accountType }} account limit of
            {{ props.numberOfUsers }} user(s) has been reached
          </p>
        </DialogDescription>
      </DialogHeader>
      <LayupSpinner v-if="isLoadingUser" />
      <template v-else>
        <div class="flex flex-col gap-4">
          <div class="space-y-2">
            <LabelComponent>Name</LabelComponent>
            <Input type="text" v-model="adminData.name.value" @blur="validateName" />
            <p v-if="adminData.name.error" class="text-sm font-medium text-destructive">
              {{ adminData.name.error }}
            </p>
          </div>
          <div class="space-y-2">
            <LabelComponent>Email</LabelComponent>
            <Input type="text" v-model="adminData.email.value" @blur="validateEmail" />
            <p v-if="adminData.email.error" class="text-sm font-medium text-destructive">
              {{ adminData.email.error }}
            </p>
          </div>
          <div class="space-y-2">
            <LabelComponent>Mobile</LabelComponent>
            <Input type="text" v-model="adminData.mobile.value" @input="formatMobile" />
            <p v-if="adminData.mobile.error" class="text-sm font-medium text-destructive">
              {{ adminData.mobile.error }}
            </p>
          </div>
          <div class="space-y-2">
            <LabelComponent>User Role</LabelComponent>
            <Select v-model="selectedRole">
              <SelectTrigger>
                <SelectValue placeholder="Select a user role" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="USER">User</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" @click="isOpen = false">Cancel</Button>
          <Button
            v-if="(!maxUsersReached && selectedAction === 'add') || selectedAction === 'edit'"
            type="button"
            class="w-auto"
            @click="onSaveMerchant"
          >
            Add User
          </Button>
        </DialogFooter>
      </template>
    </DialogContent>
  </Dialog>
  <Dialog :open="showConfirmation" @update:open="showConfirmation = $event">
    <DialogContent class="sm:max-w-[525px]">
      <DialogHeader>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogDescription>
          You are about to {{ activeState ? 'activate' : 'deactivate' }} user
          {{ adminData.name.value }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button type="button" variant="outline" @click="showConfirmation = false">Cancel</Button>
        <Button type="button" class="w-auto" @click="onConfirmDeactivation">Confirm</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NetworkError } from '@/errors';
import { addMerchantUser, getUsersByMerchant, updateUser } from '@/services/user';
import { useUserStore } from '@/stores/users';
import { isValidEmail, isValidMobile, parseMobile } from '@/utils/form.utils';
import { debounce } from '@/utils/lowDash';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import type { ColumnDef } from '@tanstack/vue-table';
import { ArrowUpDown } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, h, onMounted, ref } from 'vue';
import { useToast } from 'vue-toast-notification';
import type { ModelUserRequest, ModelUserResponse } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import TeamsDropdown from '../TeamsDropdown.vue';

const props = defineProps<{
  numberOfUsers: number;
  accountType: string;
}>();

const totalPages = ref(1);
const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);
const isLoading = ref(true);
const isLoadingUser = ref(false);
const isOpen = ref(false);
const showConfirmation = ref(false);
const activeState = ref<boolean | undefined>();
const adminData = ref({
  name: { value: '', error: '' },
  email: { value: '', error: '' },
  mobile: { value: '', error: '' },
});
const selectedRole = ref<'ADMIN' | 'USER'>('USER');
const selectedAdminId = ref('');
const users = ref([] as ModelUserResponse[]);
const selectedAction = ref<'edit' | 'add'>('add');

const maxUsersReached = computed(() => {
  const activeUsers = users.value.filter((user) => user.active);
  return activeUsers.length >= props.numberOfUsers;
});

const toast = useToast();

const teamTableColumns: ColumnDef<ModelUserResponse>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Name', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    cell: ({ getValue }) => {
      const name = getValue() as string;
      return h('div', { class: 'font-medium' }, name);
    },
  },
  {
    accessorKey: 'active',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Status', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    cell: ({ row }) => {
      const status = row.original.active ? 'Active' : 'Inactive';
      return h(Badge, { class: 'text-xs', variant: row.original.active ? 'success' : 'error' }, status);
    },
  },
  {
    accessorKey: 'merchantRole',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Type', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    cell: ({ row }) => {
      const type = row.original.merchantRole || row.original.role;
      return h('div', type);
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Email', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    cell: ({ getValue }) => {
      const email = getValue() as string;
      return h('div', email);
    },
  },
  {
    accessorKey: 'cellNumber',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Mobile', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    cell: ({ getValue }) => {
      const mobile = getValue() as string;
      return h('div', parseMobile(mobile));
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const admin: ModelUserResponse = row.original;
      return h(
        'div',
        { class: 'relative' },
        h(TeamsDropdown, {
          admin: admin,
          onUpdateEdit: (admin: ModelUserResponse) => onOpenModal('edit', admin),
          onUpdateDeactivate: (admin: ModelUserResponse, state: boolean) => onOpenConfirmation(admin, state),
        }),
      );
    },
  },
];

const loadUsers = async () => {
  try {
    isLoading.value = true;
    users.value = await getUsersByMerchant();

    addMetric('settings-page-teams', { status: 'success', channel: 'merchant', method: 'getUsersByMerchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-page-teams', {
        status: 'network_error',
        channel: 'merchant',
        method: 'getUsersByMerchant',
      });
    } else {
      createException(error);
      addMetric('settings-page-teams', { status: 'error', channel: 'merchant', method: 'getUsersByMerchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => loadUsers());

const onUpdatePage = (page: number) => {
  pageNumber.value = page;
};

const validateName = () => {
  if (adminData.value.name.value === '') {
    adminData.value.name.error = 'Please enter a name';
    return;
  }
  if (!adminData.value.name.value.split(' ')[1]) {
    adminData.value.name.error = 'Please enter a surname';
    return;
  }

  adminData.value.name.error = '';
};

const validateEmail = () => {
  if (!isValidEmail(adminData.value.email.value)) {
    adminData.value.email.error = 'Please enter a valid email address';
    return;
  }
  adminData.value.email.error = '';
};

const formatMobile = debounce(() => {
  if (isValidMobile(adminData.value.mobile.value)) {
    adminData.value.mobile.value = parseMobile(adminData.value.mobile.value);
    adminData.value.mobile.error = '';
  } else {
    adminData.value.mobile.error = 'Please enter a valid mobile number';
  }
}, 350);

const onOpenModal = (action: 'edit' | 'add', admin?: ModelUserResponse) => {
  isOpen.value = true;
  selectedAction.value = action;

  if (admin) {
    adminData.value.name.value = admin.name || '';
    adminData.value.email.value = admin.email || '';
    adminData.value.mobile.value = admin.cellNumber || '';
    selectedAdminId.value = admin._id || '';
    selectedRole.value = admin.merchantRole ? 'ADMIN' : 'USER';
  } else {
    adminData.value = {
      name: { value: '', error: '' },
      email: { value: '', error: '' },
      mobile: { value: '', error: '' },
    };
  }
};

const inputsValid = (): boolean => {
  if (adminData.value.name.value === '') {
    adminData.value.name.error = 'Please enter a name';
    return false;
  }
  if (!adminData.value.name.value.split(' ')[1]) {
    adminData.value.name.error = 'Please enter a surname';
    return false;
  }

  if (!isValidEmail(adminData.value.email.value)) {
    adminData.value.email.error = 'Please enter a valid email address';
    return false;
  }

  if (!isValidMobile(adminData.value.mobile.value)) {
    adminData.value.mobile.error = 'Please enter a valid mobile number';
    return false;
  }
  return true;
};

const onAddMerchant = async () => {
  try {
    const userStore = useUserStore();
    const { user } = storeToRefs(userStore);
    const merchant: ModelUserRequest = {
      name: adminData.value.name.value,
      email: adminData.value.email.value,
      cellNumber: adminData.value.mobile.value,
      role: 'MERCHANT',
      merchantId: user.value._id,
    };

    if (selectedRole.value === 'ADMIN') {
      merchant.merchantRole = 'ADMIN';
    }

    await addMerchantUser({ modelUserRequest: merchant });

    isOpen.value = false;
    toast.success('Added user successfully!', { position: 'top' });
    loadUsers();
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-page-security', {
        status: 'network_error',
        channel: 'merchant',
        method: 'updateMerchantUser:onAddMerchant',
      });
    } else {
      createException(error);
      addMetric('settings-page-security', {
        status: 'error',
        channel: 'merchant',
        method: 'updateMerchantUser:onAddMerchant',
      });
    }
    isOpen.value = false;
  } finally {
    isLoadingUser.value = false;
  }
};

const onEditMerchant = async () => {
  try {
    if (selectedAdminId.value === '') {
      throw new Error('User ID is required');
    }

    const merchant: ModelUserRequest = {
      name: adminData.value.name.value,
      email: adminData.value.email.value,
      cellNumber: adminData.value.mobile.value,
    };

    if (selectedRole.value === 'ADMIN') {
      merchant.merchantRole = 'ADMIN';
    }

    await updateUser({
      id: selectedAdminId.value,
      modelUserRequest: merchant,
    });

    isOpen.value = false;
    toast.success('Updated user successfully!', { position: 'top' });
    loadUsers();
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-page-security', {
        status: 'network_error',
        channel: 'merchant',
        method: 'updateMerchantUser:onEditMerchant',
      });
    } else {
      createException(error);
      addMetric('settings-page-security', {
        status: 'error',
        channel: 'merchant',
        method: 'updateMerchantUser:onEditMerchant',
      });
    }
    isOpen.value = false;
  } finally {
    isLoadingUser.value = false;
  }
};

const onSaveMerchant = async () => {
  if (inputsValid()) {
    isLoadingUser.value = true;

    if (selectedAction.value === 'edit') {
      await onEditMerchant();
    }

    if (selectedAction.value === 'add') {
      await onAddMerchant();
    }

    adminData.value = {
      name: { value: '', error: '' },
      email: { value: '', error: '' },
      mobile: { value: '', error: '' },
    };
  }
};

const onOpenConfirmation = (admin: ModelUserResponse, state: boolean) => {
  selectedAdminId.value = admin._id || '';
  adminData.value.name.value = admin.name || '';
  showConfirmation.value = true;
  activeState.value = state;
};

const onConfirmDeactivation = async () => {
  try {
    if (selectedAdminId.value === '') {
      throw new Error('User ID is required');
    }

    await updateUser({ id: selectedAdminId.value, modelUserRequest: { active: activeState.value } });
    showConfirmation.value = false;
    activeState.value = undefined;
    toast.success('Updated user successfully!', { position: 'top' });
    loadUsers();
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-page-security', {
        status: 'network_error',
        channel: 'merchant',
        method: 'updateMerchantUser',
      });
    } else {
      createException(error);
      addMetric('settings-page-security', { status: 'error', channel: 'merchant', method: 'updateMerchantUser' });
    }
  }
};
</script>
